import React, { useCallback, useMemo } from 'react';
import { Box, Center, Spinner } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import {
  useRoute, useNavigation, useIsFocused, RouteProp,
} from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { useGetAppointmentSlot, useGetSlotBookings } from '@/common/api/endpoints/appointments/appointments';
import { MeetingChrome } from '@/common/components/meeting/MeetingChrome';
import { MeetingConfirmProvider } from '@/common/components/meeting/MeetingConfirm';
import {
  ExamLeaveGuardProvider,
  useExamLeaveGuardRequestLeave,
} from '@/common/components/meeting/ExamLeaveGuard';
import {
  MeetingOpenElsewhereProvider,
  useMeetingOpenElsewhere,
} from '@/common/components/meeting/MeetingOpenElsewhere';
import { OralExamErrorState } from '@/common/components/oralExam/OralExamErrorState';
import { LecturerCohortSession } from '@/lecturer/SlotManager/ProctorExamPanel/LecturerCohortSession';
import { clearCachedJoinCreds } from '@/common/components/meeting/meetingSessionPersistence';
import {
  createLiveKitJoinHandle,
  createLiveKitSlotJoinHandle,
  getLiveKitJoinCacheKey,
} from '@/common/utils/oralExam/liveKitJoin';
import { categorizeBookings } from '@/lecturer/EventCalendar/utils/slotUtils';
import type { LecturerRouteParamList } from '@/common/navigation/RoleNavigations/Lecturer/types';

type LecturerSlotMeetingRouteProp = RouteProp<LecturerRouteParamList, 'SlotMeetingScreen'>;
type LecturerSlotMeetingNavProp = StackNavigationProp<LecturerRouteParamList, 'SlotMeetingScreen'>;

function LecturerSlotMeetingScreenBody({
  slotId,
  isFocused,
  isPending,
  bookingsData,
  liveKitOralBooking,
  bookingJoinHandle,
  bookingIds,
  slotJoinHandle,
  slotStartIso,
  slotEndIso,
  handleClose,
  handleGoToEvaluationDesk,
}: {
  slotId: string;
  isFocused: boolean;
  isPending: boolean;
  bookingsData: ReturnType<typeof useGetSlotBookings>['data'];
  liveKitOralBooking: ReturnType<typeof categorizeBookings>['active'][number] | null;
  bookingJoinHandle: ReturnType<typeof createLiveKitJoinHandle> | null;
  bookingIds: {
    courseId: string;
    moduleId: string;
    taskId: string;
    bookingId: string;
  } | null;
  slotJoinHandle: ReturnType<typeof createLiveKitSlotJoinHandle>;
  slotStartIso: string | null;
  slotEndIso: string | null;
  handleClose: () => void;
  handleGoToEvaluationDesk: (moduleId: string) => void;
}) {
  const { t } = useTranslation();
  const { openElsewhere, useThisWindow, sessionGeneration } = useMeetingOpenElsewhere();

  if (isPending && !bookingsData) {
    return (
      <Center flex={1}>
        <Spinner size="large" />
      </Center>
    );
  }

  if (openElsewhere) {
    return (
      <OralExamErrorState
        testID="meeting-open-elsewhere"
        retryTestID="meeting-open-elsewhere-use-here"
        tone="neutral"
        title={t('oralExam.openElsewhere.title', {
          defaultValue: 'This exam is open in another window.',
        })}
        retry={useThisWindow}
        retryLabel={t('oralExam.openElsewhere.useThisWindow', {
          defaultValue: 'Use this window',
        })}
      />
    );
  }

  return (
    <Box flex={1} testID="lecturer-slot-meeting-content">
      {!isFocused ? (
        <Box flex={1} testID="lecturer-slot-meeting-unfocused" />
      ) : liveKitOralBooking && bookingJoinHandle && bookingIds ? (
        <Box flex={1} testID="lecturer-cohort-session">
          <LecturerCohortSession
            key={`cohort-${sessionGeneration}`}
            slotId={slotId}
            defaultIds={bookingIds}
            defaultLiveKitJoin={bookingJoinHandle}
            slotStartIso={slotStartIso}
            slotEndIso={slotEndIso}
            onClose={handleClose}
            onGoToEvaluationDesk={handleGoToEvaluationDesk}
          />
        </Box>
      ) : (
        <Box flex={1} testID="lecturer-cohort-session">
          <LecturerCohortSession
            key={`cohort-${sessionGeneration}`}
            slotId={slotId}
            defaultIds={null}
            defaultLiveKitJoin={slotJoinHandle}
            slotStartIso={slotStartIso}
            slotEndIso={slotEndIso}
            onClose={handleClose}
            onGoToEvaluationDesk={handleGoToEvaluationDesk}
          />
        </Box>
      )}
    </Box>
  );
}

function LecturerSlotMeetingScreenChrome({
  slotId,
  isFocused,
  isPending,
  bookingsData,
  liveKitOralBooking,
  bookingJoinHandle,
  bookingIds,
  slotJoinHandle,
  slotStartIso,
  slotEndIso,
  handleClose,
  handleGoToEvaluationDesk,
}: {
  slotId: string;
  isFocused: boolean;
  isPending: boolean;
  bookingsData: ReturnType<typeof useGetSlotBookings>['data'];
  liveKitOralBooking: ReturnType<typeof categorizeBookings>['active'][number] | null;
  bookingJoinHandle: ReturnType<typeof createLiveKitJoinHandle> | null;
  bookingIds: {
    courseId: string;
    moduleId: string;
    taskId: string;
    bookingId: string;
  } | null;
  slotJoinHandle: ReturnType<typeof createLiveKitSlotJoinHandle>;
  slotStartIso: string | null;
  slotEndIso: string | null;
  handleClose: () => void;
  handleGoToEvaluationDesk: (moduleId: string) => void;
}) {
  const { requestLeave, completeLeave } = useExamLeaveGuardRequestLeave();

  const handleChromeBack = useCallback(() => {
    requestLeave().then((ok) => {
      if (ok) {
        completeLeave();
      }
    }).catch(() => {});
  }, [completeLeave, requestLeave]);

  return (
    <MeetingChrome onBack={handleChromeBack}>
      <LecturerSlotMeetingScreenBody
        slotId={slotId}
        isFocused={isFocused}
        isPending={isPending}
        bookingsData={bookingsData}
        liveKitOralBooking={liveKitOralBooking}
        bookingJoinHandle={bookingJoinHandle}
        bookingIds={bookingIds}
        slotJoinHandle={slotJoinHandle}
        slotStartIso={slotStartIso}
        slotEndIso={slotEndIso}
        handleClose={handleClose}
        handleGoToEvaluationDesk={handleGoToEvaluationDesk}
      />
    </MeetingChrome>
  );
}

export function LecturerSlotMeetingScreen() {
  const route = useRoute<LecturerSlotMeetingRouteProp>();
  const navigation = useNavigation<LecturerSlotMeetingNavProp>();
  const isFocused = useIsFocused();
  const { slotId } = route.params;

  const { data: bookingsData, isPending: bookingsPending } = useGetSlotBookings(slotId, {
    query: {
      enabled: !!slotId,
      placeholderData: (previousData) => previousData,
    },
  });

  const bookings = bookingsData?.data?.data || [];
  const { active: activeBookings } = categorizeBookings(bookings);

  const liveKitOralBooking = useMemo(
    () => activeBookings.find((b) => b.status !== 'cancelled') ?? null,
    [activeBookings],
  );

  const {
    data: slotData,
    isPending: slotPending,
    isError: slotError,
  } = useGetAppointmentSlot(slotId, {
    query: {
      enabled: !!slotId && !liveKitOralBooking,
      placeholderData: (previousData) => previousData,
      retry: false,
    },
  });

  const slotFromApi = slotData?.data?.data ?? null;

  const slotStartIso = liveKitOralBooking?.startTime ?? slotFromApi?.startTime ?? null;
  const slotEndIso = liveKitOralBooking?.endTime ?? slotFromApi?.endTime ?? null;
  const isPending = bookingsPending || (!liveKitOralBooking && slotPending);

  const bookingCourseId = liveKitOralBooking?.courseId;
  const bookingModuleId = liveKitOralBooking?.moduleId;
  const bookingTaskId = liveKitOralBooking?.taskId;
  const bookingId = liveKitOralBooking?.bookingId;

  const slotJoinHandle = useMemo(
    () => createLiveKitSlotJoinHandle(slotId),
    [slotId],
  );

  const bookingJoinHandle = useMemo(
    () => (bookingId && bookingCourseId && bookingModuleId && bookingTaskId
      ? createLiveKitJoinHandle({
        courseId: bookingCourseId,
        moduleId: bookingModuleId,
        taskId: bookingTaskId,
        bookingId,
      })
      : null),
    [bookingCourseId, bookingModuleId, bookingTaskId, bookingId],
  );

  const bookingIds = useMemo(
    () => (bookingId && bookingCourseId && bookingModuleId && bookingTaskId ? {
      courseId: bookingCourseId,
      moduleId: bookingModuleId,
      taskId: bookingTaskId,
      bookingId,
    } : null),
    [bookingCourseId, bookingModuleId, bookingTaskId, bookingId],
  );

  const handleClose = useCallback(() => {
    const slotCacheKey = getLiveKitJoinCacheKey(createLiveKitSlotJoinHandle(slotId));
    if (slotCacheKey) {
      clearCachedJoinCreds(slotCacheKey);
    }
    activeBookings.forEach((booking) => {
      const {
        bookingId, courseId, moduleId, taskId,
      } = booking;
      if (!bookingId || !courseId || !moduleId || !taskId) {
        return;
      }
      const bookingCacheKey = getLiveKitJoinCacheKey(createLiveKitJoinHandle({
        courseId, moduleId, taskId, bookingId,
      }));
      if (bookingCacheKey) {
        clearCachedJoinCreds(bookingCacheKey);
      }
    });
    navigation.popTo('EventCalendar');
  }, [activeBookings, navigation, slotId]);

  const handleGoToEvaluationDesk = useCallback((moduleId: string) => {
    navigation.popTo('EvaluationDesk', { module: { id: moduleId, title: '' } });
  }, [navigation]);

  const { t } = useTranslation();

  if (!liveKitOralBooking && slotError && !slotPending) {
    return (
      <MeetingOpenElsewhereProvider>
        <MeetingConfirmProvider>
          <OralExamErrorState
            testID="lecturer-slot-not-found"
            tone="neutral"
            title={t('common.messages.error')}
            leave={handleClose}
            leaveLabel={t('common.buttons.back', { defaultValue: 'Back' })}
          />
        </MeetingConfirmProvider>
      </MeetingOpenElsewhereProvider>
    );
  }

  // LecturerNavigation hides its left sidebar for this route (full-screen meeting),
  // but its top header bar has no back affordance — a lecturer who deep-links
  // straight into a slot (bookmark, shared URL, browser refresh) had no way back
  // to the calendar. MeetingChrome supplies the single logo-left bar + back
  // control for standalone meeting screens (mirrors the student SlotMeetingScreen).
  return (
    <MeetingOpenElsewhereProvider>
      <MeetingConfirmProvider>
        <ExamLeaveGuardProvider navigation={navigation} onLeaveConfirmed={handleClose}>
          <LecturerSlotMeetingScreenChrome
            slotId={slotId}
            isFocused={isFocused}
            isPending={isPending}
            bookingsData={bookingsData}
            liveKitOralBooking={liveKitOralBooking}
            bookingJoinHandle={bookingJoinHandle}
            bookingIds={bookingIds}
            slotJoinHandle={slotJoinHandle}
            slotStartIso={slotStartIso}
            slotEndIso={slotEndIso}
            handleClose={handleClose}
            handleGoToEvaluationDesk={handleGoToEvaluationDesk}
          />
        </ExamLeaveGuardProvider>
      </MeetingConfirmProvider>
    </MeetingOpenElsewhereProvider>
  );
}
